import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { FileCategory, ProjectFile } from '../../models';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';

@Component({
	selector: 'app-media-picker',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, FormsModule, InputText],
	templateUrl: './media-picker.html',
	styleUrl: './media-picker.scss',
})
export class MediaPickerComponent {
	visible = model(false);
	categories = input.required<FileCategory[]>();
	currentValue = input('');

	fileSelected = output<string>();
	filesSelected = output<string[]>();
	multi = input(false);

	private filesService = inject(EditorFilesService);
	protected searchQuery = signal('');
	protected selectedUrls = signal<Set<string>>(new Set());

	protected filteredFiles = computed(() => {
		const grouped = this.filesService.groupedFiles();
		const cats = this.categories();
		const query = this.searchQuery().toLowerCase().trim();

		// Collect files, deduplicate by baseUrl
		const seen = new Set<string>();
		const files: ProjectFile[] = [];
		for (const cat of cats) {
			for (const file of grouped[cat] ?? []) {
				if (seen.has(file.baseUrl)) continue;
				seen.add(file.baseUrl);
				files.push(file);
			}
		}

		if (!query) return files;
		return files.filter(f => f.name.toLowerCase().includes(query) || f.baseUrl.toLowerCase().includes(query));
	});

	protected isSelected(file: ProjectFile): boolean {
		if (this.multi()) return this.selectedUrls().has(file.baseUrl);
		const current = this.currentValue();
		if (!current) return false;
		return file.baseUrl === current;
	}

	protected selectFile(file: ProjectFile): void {
		if (this.multi()) {
			this.selectedUrls.update(set => {
				const next = new Set(set);
				if (next.has(file.baseUrl)) next.delete(file.baseUrl);
				else next.add(file.baseUrl);
				return next;
			});
		} else {
			this.fileSelected.emit(file.baseUrl);
			this.visible.set(false);
			this.searchQuery.set('');
		}
	}

	protected onHide(): void {
		this.searchQuery.set('');
		this.selectedUrls.set(new Set());
	}

	protected confirmMulti(): void {
		this.filesSelected.emit([...this.selectedUrls()]);
		this.visible.set(false);
		this.searchQuery.set('');
		this.selectedUrls.set(new Set());
	}

	protected isImageCategory(file: ProjectFile): boolean {
		return file.category === 'images';
	}

	protected isVideoCategory(file: ProjectFile): boolean {
		return file.category === 'video' || file.category === 'mask' || file.category === 'video-mask';
	}

	protected stripExt(name: string): string {
		const dotIdx = name.lastIndexOf('.');
		return dotIdx > 0 ? name.substring(0, dotIdx) : name;
	}
}
